/**
 * MyRoute — driver-facing "today's route" page with SWIPE-TO-STATUS:
 *   • Right swipe on a package card → mark as delivered (status="done")
 *   • Left swipe → mark as awizo (status="skipped")
 *   • Tap card → open detail modal / expand
 * Every status update goes live to backend so admin sees progress in real time.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Truck, Car, PackageCheck, PackageX, Wallet, ChevronRight, Loader2, MapPin,
  Check, X, Phone, Clock, User2, Undo2, Mail, RotateCcw, Repeat, Search, Trophy,
  Volume2, VolumeX, FileText, Download, PackageSearch,
} from "lucide-react";
import { Routes as RoutesAPI } from "../lib/api";
import { API } from "../lib/api";
import { getCurrentLocation } from "../lib/geo";
import { playSuccess, playError, playClick, playSettle, isSfxMuted, setSfxMuted } from "../lib/sfx";
import SettleSummaryModal from "../components/SettleSummaryModal";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../contexts/AuthContext";
import { emitXp } from "../components/XpBurst";
import { PackagePhoto } from "../components/loading/PackagePhoto";
import { slotLabel, bagHex } from "../components/loading/cargoSlots";
import { CargoGroupsDialog } from "../components/loading/CargoGroupsDialog";
import { NextParcelBar } from "../components/loading/NextParcelBar";
import { haptic, HAPTIC } from "../lib/haptics";
import { PageSkeleton } from "../components/PageSkeleton";

// Extract phone digits from raw string (handles "+48 601 234 567", "601-234-567" etc.)
const stripPhone = (p) => (p || "").toString().replace(/[^\d+]/g, "");

// Build a full postal-style address: "ul. Xyz 12, 71-604 Szczecin"
const fullAddress = (s) => {
  const parts = [];
  if (s.address_text) parts.push(s.address_text);
  const zipCity = [s.zip, s.city].filter(Boolean).join(" ").trim();
  if (zipCity) parts.push(zipCity);
  return parts.join(", ");
};

// Multipack grouping key — MUST match backend `_normalize_stop_addr`
// (case+whitespace insensitive, slash-sensitive).
const stopGroupKey = (s) => {
  const parts = [
    (s.address_text || "").trim().toLowerCase(),
    (s.zip || "").trim().toLowerCase(),
    (s.city || "").trim().toLowerCase(),
  ];
  const k = parts.join("|").replace(/\s+/g, " ");
  // Lone stops (no address) get a unique key so they never merge.
  if (!k.replace(/\|/g, "").trim()) return `__lone__${s.final_order}`;
  return k;
};

// Re-order stops so multipack siblings sit next to each other.
// Groups appear in ascending order of their first-member final_order;
// within a group, stops are ordered by their own final_order.
const groupStopsByAddress = (stops) => {
  const map = new Map();
  stops.forEach((s) => {
    const key = stopGroupKey(s);
    const g = map.get(key);
    if (g) {
      g.stops.push(s);
      g.first = Math.min(g.first, s.final_order || 999999);
    } else {
      map.set(key, { stops: [s], first: s.final_order || 999999 });
    }
  });
  return Array.from(map.values())
    .sort((a, b) => a.first - b.first)
    .flatMap((g) => g.stops.sort(
      (a, b) => (a.final_order || 0) - (b.final_order || 0),
    ));
};

const UNDELIVERED_REASONS = [
  { id: "awizo",      label: "Awizo",              icon: Mail,    hint: "Klient nieobecny — awizo w skrzynce", tone: "from-amber-500 to-orange-700" },
  { id: "return",     label: "Zwrot do nadawcy",   icon: RotateCcw, hint: "Paczka wraca do nadawcy",             tone: "from-red-500 to-rose-800" },
  { id: "redelivery", label: "Powtórne doręczenie",icon: Repeat,  hint: "Ustalone kolejne podejście",            tone: "from-purple-500 to-fuchsia-800" },
];

const REASON_LABEL = Object.fromEntries(UNDELIVERED_REASONS.map((r) => [r.id, r.label]));

// ─── Driver color palette ────────────────────────────────────────────────
// Same 6-tone palette used by Work.jsx audit pills → keeps color identity
// consistent between admin and driver views. Green = "Ty" gets pinned first
// as a solid brand-y accent; the rest are hashed off the user_id so multiple
// drivers on the same route stay visually distinct.
const DRIVER_TONES = [
  { bg: "bg-emerald-500 text-white ring-emerald-600", hex: "#10b981", soft: "bg-emerald-100 text-emerald-800 ring-emerald-300" },
  { bg: "bg-violet-500 text-white ring-violet-600",   hex: "#8b5cf6", soft: "bg-violet-100 text-violet-800 ring-violet-300"   },
  { bg: "bg-blue-500 text-white ring-blue-600",       hex: "#3b82f6", soft: "bg-blue-100 text-blue-800 ring-blue-300"         },
  { bg: "bg-pink-500 text-white ring-pink-600",       hex: "#ec4899", soft: "bg-pink-100 text-pink-800 ring-pink-300"         },
  { bg: "bg-cyan-500 text-white ring-cyan-600",       hex: "#06b6d4", soft: "bg-cyan-100 text-cyan-800 ring-cyan-300"         },
  { bg: "bg-amber-500 text-white ring-amber-600",     hex: "#f59e0b", soft: "bg-amber-100 text-amber-800 ring-amber-300"      },
];
const _toneCache = new Map();
function driverTone(userId, isMe) {
  // "Ty" is always green so the driver instantly recognises their own actions.
  if (isMe) return DRIVER_TONES[0];
  if (!userId) return { bg: "bg-zinc-300 text-zinc-700 ring-zinc-400", hex: "#71717a", soft: "bg-zinc-100 text-zinc-700 ring-zinc-300" };
  if (_toneCache.has(userId)) return _toneCache.get(userId);
  // Skip index 0 (reserved for "me") — hash into 1..N.
  let h = 0;
  for (const c of userId) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  const idx = 1 + (Math.abs(h) % (DRIVER_TONES.length - 1));
  const tone = DRIVER_TONES[idx];
  _toneCache.set(userId, tone);
  return tone;
}

function DriverChip({ driver, dense = false }) {
  const tone = driverTone(driver.user_id, driver.is_me);
  return (
    <span
      data-testid={`driver-chip-${driver.user_id}`}
      className={`inline-flex items-center gap-1 rounded-full ring-1 font-bold ${tone.bg} ${
        dense ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"
      }`}
      title={
        `${driver.is_me ? "Ty" : (driver.user_name || "kierowca")} · ` +
        `${driver.delivered} doręczone · ${driver.awizo} niedoręczone`
      }
    >
      <User2 className={dense ? "w-2.5 h-2.5" : "w-3 h-3"} />
      <span className="max-w-[110px] truncate">
        {driver.is_me ? "Ty" : (driver.user_name || "Kierowca")}
      </span>
      {(driver.delivered > 0 || driver.awizo > 0) && (
        <span className="opacity-80 font-mono normal-case">
          · {driver.delivered}
          {driver.awizo > 0 && <span className="opacity-70">/{driver.awizo}</span>}
        </span>
      )}
    </span>
  );
}


export default function MyRoute({ embedded = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const myUserId = user?.user_id || null;
  const [assignments, setAssignments] = useState([]); // today's non-settled list
  const [selectedId, setSelectedId] = useState(null); // which assignment is open
  const [asg, setAsg] = useState(null);
  const [stops, setStops] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [drivers, setDrivers] = useState([]); // roster of couriers on the currently open plan
  const [loading, setLoading] = useState(true);
  const [reasonFor, setReasonFor] = useState(null); // stop pending reason pick
  const [query, setQuery] = useState("");
  const [settling, setSettling] = useState(false);
  const [settleSummary, setSettleSummary] = useState(null);
  const [sfxOff, setSfxOff] = useState(() => isSfxMuted());
  const [cargoOpen, setCargoOpen] = useState(false);
  const { confirm } = useConfirm();

  // Pull today's assignments (including settled ones — they stay visible until
  // end-of-day per user request). Auto-open the single OPEN one for quick
  // start; if everything is settled we fall through to picker so the driver
  // sees the "rozliczone" state cards instead of an empty screen.
  const load = async () => {
    setLoading(true);
    try {
      const d = await RoutesAPI.listMyWork();
      const list = d.assignments || [];
      setAssignments(list);
      const openOnes = list.filter((a) => !a.settled_at);
      if (openOnes.length === 1) {
        // Auto-open the sole unfinished assignment for a quick start
        await openAssignment(openOnes[0].id);
      } else if (list.length === 0) {
        setAsg(null);
        setStops([]);
        setSelectedId(null);
      }
      // 2+ open OR only settled → user picks from list; do not auto-open
    } catch (e) {
      toast.error("Nie mogę pobrać trasy");
    } finally {
      setLoading(false);
    }
  };
  const openAssignment = async (id) => {
    try {
      const pkg = await RoutesAPI.listAssignmentPackages(id);
      setAsg(pkg.assignment);
      setPlanId(pkg.plan_id);
      setStops(pkg.stops || []);
      setDrivers(pkg.drivers || []);
      setSelectedId(id);
      setQuery(""); // reset search when switching assignments
    } catch (e) {
      toast.error("Nie mogę pobrać paczek zlecenia");
    }
  };
  const backToPicker = () => {
    setSelectedId(null);
    setAsg(null);
    setStops([]);
    setDrivers([]);
    setPlanId(null);
    setQuery("");
    // Also refresh list so progress counters reflect what was just changed
    load();
  };
  useEffect(() => { load(); }, []);

  const applyStatus = async (stop, status, reason = null) => {
    // Optimistic UI + audible feedback for the driver's muscle memory.
    if (status === "done") {
      playSuccess();
      haptic(HAPTIC.done);
      // Only emit the XP burst when a stop transitions FROM non-done TO done —
      // toggling the same stop back-and-forth shouldn't inflate the counter.
      if (stop.status !== "done") emitXp(1);
    }
    else if (status === "skipped") { playError(); haptic(HAPTIC.skip); }
    else playClick();
    setStops((prev) => prev.map((s) =>
      s.final_order === stop.final_order
        ? { ...s, status, undelivered_reason: reason || undefined }
        : s,
    ));
    try {
      // Grab GPS non-blockingly — audit log for admin. `null` when denied.
      const location = status === "pending" ? null : await getCurrentLocation(3500);
      await RoutesAPI.updateStopStatus(planId, stop.final_order, status, reason, location);
      if (reason) toast.success(`Niedoręczona: ${REASON_LABEL[reason]}`);
    } catch (e) {
      toast.error("Błąd zapisu — odświeżam");
      load();
    }
  };

  const requestUndeliveredReason = (stop) => setReasonFor(stop);
  const commitUndelivered = (reason) => {
    if (!reasonFor) return;
    applyStatus(reasonFor, "skipped", reason);
    setReasonFor(null);
  };

  const settleThisAssignment = async () => {
    if (!asg?.id) return;
    if (!(await confirm(
      "Rozliczyć zlecenie? Doręczone paczki zasilą Twój portfel, XP i licznik. Zobaczysz podsumowanie z sumą pobrań do wpłaty.",
      { confirmLabel: "Rozlicz", tone: "info" },
    ))) return;
    setSettling(true);
    try {
      const r = await RoutesAPI.settleAssignment(asg.id);
      playSettle();
      toast.success(
        `Zlecenie rozliczone! +${(r.wallet_gain || 0).toLocaleString("pl-PL")} zł`,
        { duration: 4000 },
      );
      setSettleSummary(r);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd rozliczenia");
    } finally {
      setSettling(false);
    }
  };

  if (loading) {
    return <PageSkeleton label="Ładuję trasę…" />;
  }

  if (assignments.length === 0) {
    return (
      <div className={`${embedded ? "" : "p-6 md:p-10"}`} data-testid="myroute-empty">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          // Moja trasa
        </div>
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight mb-4">
          Brak zleceń na dziś
        </h1>
        <p className="text-sm text-zinc-600 max-w-md">
          Koordynator jeszcze nie przypisał Ci zleceń na dzisiaj.
        </p>
      </div>
    );
  }

  // Picker view — driver has 2+ assignments OR nothing is currently open
  if (!selectedId) {
    return (
      <div className={`${embedded ? "" : "p-3 md:p-6"} space-y-4`} data-testid="myroute-picker">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            // Twoje zlecenia na dzisiaj
          </div>
          <h2 className="font-heading font-black text-xl uppercase tracking-tight mt-0.5">
            Wybierz zlecenie do doręczania
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Możesz przełączać się między zleceniami. Rozliczone zostają widoczne do końca dnia — rano znikają z listy.
          </p>
        </div>
        <div className="space-y-2" data-testid="myroute-assignment-list">
          {assignments.map((a) => {
            const pct = a.total_packages > 0
              ? Math.round(((a.delivered + a.awizo) / a.total_packages) * 100)
              : 0;
            const isSettled = !!a.settled_at;
            return (
              <button
                key={a.id}
                onClick={() => { playClick(); openAssignment(a.id); }}
                data-testid={`asg-pick-${a.id}`}
                className={`w-full text-left p-4 rounded-2xl ring-1 hover:shadow-md transition-all active:scale-[0.99] ${
                  isSettled
                    ? "bg-emerald-50/60 ring-emerald-200 hover:ring-emerald-400"
                    : "bg-white ring-zinc-200 hover:ring-orange-400"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl text-white flex items-center justify-center shrink-0 ${
                    isSettled
                      ? "bg-gradient-to-br from-emerald-500 to-green-800"
                      : "bg-gradient-to-br from-orange-500 to-red-700"
                  }`}>
                    {isSettled ? <Trophy className="w-5 h-5" /> : <Truck className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-black uppercase tracking-tight truncate flex items-center gap-2 flex-wrap">
                      {a.plan_name || "Bez nazwy"}
                      {isSettled && (
                        <span
                          data-testid={`asg-settled-${a.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] font-mono uppercase tracking-widest"
                          title={`Rozliczone ${new Date(a.settled_at).toLocaleString("pl-PL")}`}
                        >
                          <Trophy className="w-2.5 h-2.5" /> Rozliczone
                        </span>
                      )}
                    </div>
                    {/* Per-driver colored chips replace the generic "Współdzielone"
                        pill: at a glance you see WHO is on this route and their
                        current tally. "Ty" is always green. */}
                    {(a.drivers || []).length > 1 && (
                      <div
                        className="flex flex-wrap gap-1 mt-1"
                        data-testid={`asg-drivers-${a.id}`}
                      >
                        {a.drivers.map((d) => (
                          <DriverChip key={d.user_id} driver={d} dense />
                        ))}
                      </div>
                    )}
                    <div className="text-[11px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                      {a.vehicle_plate && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white">
                          <Car className="w-3 h-3" /> {a.vehicle_plate}
                        </span>
                      )}
                      <span>{a.total_packages} paczek</span>
                      <span className="text-emerald-700">· {a.delivered} doręczone</span>
                      {a.awizo > 0 && <span className="text-amber-700">· {a.awizo} awizo</span>}
                      {a.pending > 0 && <span className="text-cyan-700">· {a.pending} do zrobienia</span>}
                    </div>
                    {/* progress bar + % */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-700 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span
                        className="text-[10px] font-mono font-bold text-emerald-700 tabular-nums"
                        data-testid={`asg-pct-${a.id}`}
                      >
                        {pct}%
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-400 shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (!asg) {
    return (
      <div className="p-6 md:p-10">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
      </div>
    );
  }

  // In multi-driver routes the top widgets should show ONLY this courier's
  // work — so my earnings, my delivered count and my collected COD match the
  // exact number of packages I personally handled. The plan-wide totals stay
  // useful for the progress bar (which we still fill from the full stop set).
  const isMultiDriver = (drivers || []).length > 1;
  const myStops = isMultiDriver
    ? stops.filter((s) =>
        // Count as "mine" if I stamped the status OR it's still open (I might do it).
        s.status_by === myUserId || (s.status || "pending") === "pending",
      )
    : stops;
  const done = stops.filter((s) =>
    s.status === "done" && (!isMultiDriver || s.status_by === myUserId),
  ).length;
  const skipped = stops.filter((s) =>
    s.status === "skipped" && (!isMultiDriver || s.status_by === myUserId),
  ).length;
  const pending = stops.filter((s) => (s.status || "pending") === "pending").length;
  const totalTile = isMultiDriver ? myStops.length : stops.length;

  // COD math is driver-scoped in multi-driver mode: my potential pobrania =
  // stops assigned to me (delivered by me OR still pending); my collected =
  // COD on stops I marked as done.
  const codTotal = myStops.reduce((a, s) => a + (Number(s.cod_amount) || 0), 0);
  const codCollected = stops
    .filter((s) => s.status === "done" && (!isMultiDriver || s.status_by === myUserId))
    .reduce((a, s) => a + (Number(s.cod_amount) || 0), 0);

  // Search filter across name, address, phone, parcel number
  const q = query.trim().toLowerCase();
  const filteredStops = groupStopsByAddress(
    q
      ? stops.filter((s) => {
          const hay = [
            s.client_name,
            s.address_text,
            s.city,
            s.zip,
            s.phone,
            s.parcel_number,
            String(s.final_order || ""),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        })
      : stops,
  );

  return (
    <div className={`${embedded ? "" : "p-3 md:p-6"} space-y-4 pb-24`} data-testid="myroute-page">
      {/* Back to picker — only when there are multiple assignments today */}
      {assignments.length > 1 && (
        <button
          onClick={backToPicker}
          data-testid="myroute-back-to-picker"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-600 hover:text-zinc-900 bg-white ring-1 ring-zinc-200 px-3 py-1.5 rounded-full active:scale-95 transition-transform"
        >
          <Undo2 className="w-3.5 h-3.5" /> Zmień zlecenie · {assignments.length} dostępnych
        </button>
      )}
      {/* Header */}
      {!embedded && (
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          // Moja trasa na dziś · Swipe → doręczono · Swipe ← awizo
        </div>
        <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mt-0.5">
          {asg.plan_name || "Trasa"}
        </h1>
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500 flex-wrap font-mono">
          {asg.assigned_at && <span>{new Date(asg.assigned_at).toLocaleDateString("pl-PL")}</span>}
          {asg.vehicle_plate && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-900 text-white">
              <Car className="w-3 h-3" /> {asg.vehicle_plate}
            </span>
          )}
        </div>
      </div>
      )}
      {embedded && asg?.plan_name && (
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
          // {asg.plan_name}
          {asg.vehicle_plate && ` · ${asg.vehicle_plate}`}
        </div>
      )}

      {/* Manifest downloads — identyczne opcje jak w menu Trasy. */}
      {planId && (
        <div className="flex flex-wrap gap-2" data-testid="myroute-downloads">
          <a
            href={`${API}/routes/plans/${planId}/export-spoke.pdf`}
            download
            data-testid="btn-myroute-export-spoke"
            title="Manifest PDF dla Spoke (importuj jako 'Import Manifest from PDF')"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 shadow-sm active:scale-95 transition-transform"
          >
            <FileText className="w-3 h-3" /> Manifest PDF
          </a>
          <a
            href={`${API}/routes/plans/${planId}/export-spoke.pdf?simple=1`}
            download
            data-testid="btn-myroute-export-spoke-simple"
            title="Uproszczony manifest PDF (bez tabeli)"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100 active:scale-95 transition-transform"
          >
            <Download className="w-3 h-3" /> Uproszczony
          </a>
          <a
            href={`${API}/routes/plans/${planId}/export.xlsx`}
            download
            data-testid="btn-myroute-export-xlsx"
            title="Pobierz Excel z paczkami trasy"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100 active:scale-95 transition-transform"
          >
            <Download className="w-3 h-3" /> Excel
          </a>
          {stops.some((s) => s.cargo_slot) && (
            <button
              type="button"
              onClick={() => setCargoOpen(true)}
              data-testid="btn-myroute-cargo-groups"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest bg-orange-600 text-white hover:bg-orange-700 shadow-sm active:scale-95 transition-transform"
            >
              <PackageSearch className="w-3 h-3" /> Według miejsca
            </button>
          )}
        </div>
      )}
      {cargoOpen && <CargoGroupsDialog stops={stops} onClose={() => setCargoOpen(false)} />}

      {/* Multi-driver roster — colored chips for every courier on this route.
          Green = you; other colors are stable per user_id so they match the
          "Doręczył: X" badges on each package below. */}
      {isMultiDriver && (
        <div
          className="flex flex-wrap gap-1.5 items-center p-2.5 rounded-2xl bg-white ring-1 ring-zinc-200"
          data-testid="myroute-drivers-roster"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 pr-1">
            // Trasa wspólna:
          </span>
          {drivers.map((d) => (
            <DriverChip key={d.user_id} driver={d} />
          ))}
        </div>
      )}

      {/* Progress bar for the currently open assignment */}
      {stops.length > 0 && (() => {
        const total = stops.length;
        const doneCnt = stops.filter((s) => s.status === "done").length;
        const skipCnt = stops.filter((s) => s.status === "skipped").length;
        const pct = total ? Math.round(((doneCnt + skipCnt) / total) * 100) : 0;
        return (
          <div className="space-y-1" data-testid="myroute-progress">
            <div className="flex items-center justify-between text-[11px] font-mono">
              <span className="uppercase tracking-widest text-zinc-500">Postęp trasy</span>
              <span className="font-bold text-zinc-900 tabular-nums">
                {doneCnt + skipCnt} / {total}
                <span className="ml-2 text-emerald-700">{pct}%</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-zinc-100 overflow-hidden ring-1 ring-zinc-200">
              <div className="h-full flex">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-700 transition-all"
                  style={{ width: `${(doneCnt / total) * 100}%` }}
                />
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
                  style={{ width: `${(skipCnt / total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Summary tiles — in multi-driver mode counters are scoped to YOU
          so you see your own workload / earnings potential. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Tile
          icon={MapPin}
          label={isMultiDriver ? "Moje" : "Wszystkie"}
          value={totalTile}
          tone="zinc"
        />
        <Tile icon={PackageCheck} label={isMultiDriver ? "Doręczone (Ja)" : "Doręczone"} value={done} tone="emerald" />
        <Tile icon={PackageX} label={isMultiDriver ? "Niedoręczone (Ja)" : "Niedoręczone"} value={skipped} tone="amber" />
        <Tile icon={Clock} label="Do zrobienia" value={pending} tone="cyan" />
      </div>

      {/* COD */}
      {codTotal > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-800 text-white p-4 ring-1 ring-white/10 flex items-center gap-4">
          <Wallet className="w-6 h-6" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
              {isMultiDriver ? "Pobrania (Twoje)" : "Pobrania"}
            </div>
            <div className="text-lg font-heading font-black">
              {codCollected.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} / {codTotal.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] font-mono text-white/60">zostało</div>
            <div className="text-lg font-heading font-black text-amber-200">
              {(codTotal - codCollected).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
            </div>
          </div>
        </div>
      )}

      {/* Swipe hint */}
      <div className="rounded-xl bg-gradient-to-r from-emerald-50 via-white to-amber-50 ring-1 ring-zinc-200 p-3 flex items-center justify-between text-xs text-zinc-700">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
            <Check className="w-3 h-3" /> Swipe →
          </span>
          <span>doręczono</span>
        </div>
        <div className="flex items-center gap-2">
          <span>niedoręczona</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
            <X className="w-3 h-3" /> ← Swipe
          </span>
        </div>
      </div>

      {/* Search filter */}
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Szukaj po nazwisku, adresie, telefonie, nr paczki…"
            data-testid="myroute-search"
            className="w-full pl-10 pr-10 py-2.5 rounded-2xl bg-white ring-1 ring-zinc-200 focus:ring-2 focus:ring-orange-400 focus:outline-none text-sm placeholder:text-zinc-400"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="myroute-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100 text-zinc-500"
              title="Wyczyść"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          onClick={() => {
            const nextMuted = !sfxOff;
            setSfxMuted(nextMuted);
            setSfxOff(nextMuted);
            if (!nextMuted) playClick();
          }}
          data-testid="myroute-sfx-toggle"
          className="p-2.5 rounded-2xl bg-white ring-1 ring-zinc-200 hover:bg-zinc-100 active:scale-90 transition-transform shrink-0"
          title={sfxOff ? "Włącz dźwięki" : "Wycisz dźwięki"}
        >
          {sfxOff ? <VolumeX className="w-4 h-4 text-zinc-500" /> : <Volume2 className="w-4 h-4 text-emerald-600" />}
        </button>
      </div>

      {/* Package stack */}
      {!query && <NextParcelBar stop={[...stops].sort((a, b) => a.final_order - b.final_order).find((s) => (s.status || "pending") === "pending")} />}
      <div className="space-y-2">
        {stops.length === 0 && (
          <div className="p-8 text-center text-sm text-zinc-500 rounded-2xl bg-white ring-1 ring-zinc-200">
            Brak paczek na trasie.
          </div>
        )}
        {stops.length > 0 && filteredStops.length === 0 && (
          <div className="p-8 text-center text-sm text-zinc-500 rounded-2xl bg-white ring-1 ring-zinc-200">
            Brak wyników dla &bdquo;{query}&rdquo;.
          </div>
        )}
        {filteredStops.map((s) => (
          <SwipeCard
            key={s.final_order}
            stop={s}
            myUserId={myUserId}
            onSwipeRight={() => applyStatus(s, "done")}
            onSwipeLeft={() => requestUndeliveredReason(s)}
            onReset={() => applyStatus(s, "pending")}
          />
        ))}
      </div>

      {stops.length > 0 && pending === 0 && !asg.settled_at && (
        <button
          onClick={settleThisAssignment}
          disabled={settling}
          data-testid="myroute-settle"
          className="fixed bottom-20 left-4 right-4 md:static inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-700 text-white font-bold uppercase tracking-widest text-sm shadow-2xl hover:from-emerald-600 hover:to-green-800 z-30 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 transition-transform"
        >
          {settling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
          {settling ? "Rozliczam…" : "Rozlicz zlecenie"}
          {!settling && <ChevronRight className="w-4 h-4" />}
        </button>
      )}

      {asg?.settled_at && (
        <div
          className="rounded-2xl bg-emerald-50 border-2 border-emerald-300 p-4 flex items-center gap-3"
          data-testid="myroute-settled-info"
        >
          <Trophy className="w-6 h-6 text-emerald-700" />
          <div className="flex-1">
            <div className="text-sm font-heading font-black text-emerald-900 uppercase tracking-wide">
              Zlecenie rozliczone
            </div>
            <div className="text-xs text-emerald-700 font-mono">
              {new Date(asg.settled_at).toLocaleString("pl-PL")}
            </div>
          </div>
        </div>
      )}

      {/* Undelivered reason picker */}
      {reasonFor && (
        <UndeliveredReasonModal
          stop={reasonFor}
          onCancel={() => setReasonFor(null)}
          onPick={commitUndelivered}
        />
      )}

      <SettleSummaryModal
        open={!!settleSummary}
        result={settleSummary}
        planName={asg?.plan_name}
        onClose={() => {
          setSettleSummary(null);
          // Kick user back to picker so they can pick another assignment
          // (or see the empty state if that was the last one for today).
          backToPicker();
        }}
      />
    </div>
  );
}

function UndeliveredReasonModal({ stop, onCancel, onPick }) {
  return (
    <div
      onClick={onCancel}
      className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-3"
      data-testid="reason-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-200"
      >
        <div className="p-5 border-b border-zinc-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            // Paczka #{stop.final_order} niedoręczona — wybierz powód
          </div>
          <h2 className="font-heading font-black uppercase tracking-tight text-xl mt-1">
            Dlaczego niedoręczona?
          </h2>
        </div>
        <div className="p-3 grid grid-cols-1 gap-2">
          {UNDELIVERED_REASONS.map((r) => (
            <button
              key={r.id}
              onClick={() => onPick(r.id)}
              data-testid={`btn-reason-${r.id}`}
              className={`relative overflow-hidden group rounded-2xl p-4 text-left ring-1 ring-zinc-200 hover:ring-zinc-400 transition-all hover:scale-[1.01]`}
            >
              <div className={`absolute inset-0 opacity-15 bg-gradient-to-br ${r.tone}`} />
              <div className="relative flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${r.tone} text-white flex items-center justify-center ring-1 ring-white/25 shadow-md`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-black uppercase tracking-tight">{r.label}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">{r.hint}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-400" />
              </div>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-zinc-100">
          <button onClick={onCancel} data-testid="btn-reason-cancel"
                  className="w-full py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-sm font-bold uppercase tracking-widest">
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SwipeCard — pointer-event based swipe (works on touch + mouse), no deps.
// ═════════════════════════════════════════════════════════════════════════
const SWIPE_THRESHOLD = 100; // px

function SwipeCard({ stop, myUserId, onSwipeRight, onSwipeLeft, onReset }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [flash, setFlash] = useState(null); // 'good' | 'bad' — brief pulse animation
  const [menuOpen, setMenuOpen] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const moved = useRef(false);
  const ref = useRef(null);
  const prevStatus = useRef(stop.status);
  const status = stop.status || "pending";
  const isSettled = status === "done" || status === "skipped";

  // Trigger a short pulse when the status transitions (either direction).
  useEffect(() => {
    if (prevStatus.current !== stop.status) {
      if (stop.status === "done") setFlash("good");
      else if (stop.status === "skipped") setFlash("bad");
      prevStatus.current = stop.status;
      const t = setTimeout(() => setFlash(null), 500);
      return () => clearTimeout(t);
    }
  }, [stop.status]);

  const start = (clientX, clientY) => {
    // Always track the tap origin so tap-to-menu works even on settled cards.
    // Dragging (for swipe) is still disabled once the stop is settled.
    startX.current = clientX;
    startY.current = clientY;
    moved.current = false;
    setDragging(true);
  };
  const move = (clientX, clientY) => {
    if (!dragging) return;
    const dxLocal = clientX - startX.current;
    const dyLocal = clientY - startY.current;
    // If the user moved more than a few pixels, it's a drag, not a tap.
    if (Math.abs(dxLocal) > 8 || Math.abs(dyLocal) > 8) moved.current = true;
    // Freeze horizontal translation on settled cards so it looks like they
    // aren't draggable, while still allowing the tap detection above.
    if (!isSettled) setDx(dxLocal);
  };
  const end = () => {
    if (!dragging) return;
    setDragging(false);
    if (moved.current && !isSettled) {
      if (dx > SWIPE_THRESHOLD) {
        onSwipeRight();
      } else if (dx < -SWIPE_THRESHOLD) {
        onSwipeLeft();
      }
    } else if (!moved.current) {
      // Tap-without-swipe → toggle the context menu (phone / nav actions)
      playClick();
      setMenuOpen((v) => !v);
    }
    setDx(0);
  };

  const bg =
    status === "done"    ? "bg-emerald-50 ring-emerald-200" :
    status === "skipped" ? "bg-amber-50 ring-amber-200"     :
    "bg-white ring-zinc-200";

  const revealLeft = dx > 20;  // pulling right → reveal green
  const revealRight = dx < -20; // pulling left → reveal amber
  const phoneDigits = stripPhone(stop.phone);
  const navUrl = fullAddress(stop)
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress(stop))}`
    : null;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ring-1 ${bg} ${
        flash === "good" ? "animate-[flash-good_0.5s_ease-out]" :
        flash === "bad"  ? "animate-[flash-bad_0.5s_ease-out]"  : ""
      }`}
      data-testid={`swipe-card-${stop.final_order}`}
    >
      {/* Reveal layers — pointer-events-none when opacity-0 so they don't
          block clicks on the ctx-menu that lives BELOW the card body. */}
      <div className={`absolute inset-y-0 left-0 flex items-center pl-4 text-white font-heading font-black uppercase tracking-wider transition-opacity ${revealLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
           style={{ background: "linear-gradient(to right, #10b981, transparent)", width: "50%" }}>
        <Check className="w-6 h-6 mr-2" /> Doręczono
      </div>
      <div className={`absolute inset-y-0 right-0 flex items-center justify-end pr-4 text-white font-heading font-black uppercase tracking-wider transition-opacity ${revealRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
           style={{ background: "linear-gradient(to left, #f59e0b, transparent)", width: "50%" }}>
        Niedoręczona <X className="w-6 h-6 ml-2" />
      </div>

      {/* Card body */}
      <div
        ref={ref}
        onPointerDown={(e) => start(e.clientX, e.clientY)}
        onPointerMove={(e) => move(e.clientX, e.clientY)}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        className="relative flex items-center gap-3 p-3 select-none touch-pan-y cursor-pointer active:cursor-grabbing"
        style={{
          transform: dragging ? `translateX(${dx}px)` : "translateX(0)",
          transition: dragging ? "none" : "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-heading font-black shrink-0 ${
          status === "done" ? "bg-emerald-500 text-white"
          : status === "skipped" ? "bg-amber-500 text-white"
          : "bg-zinc-100 text-zinc-600"
        }`}>
          {status === "done" ? <Check className="w-4 h-4" />
           : status === "skipped" ? <X className="w-4 h-4" />
           : stop.final_order}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-900 truncate" data-testid={`stop-name-${stop.final_order}`}>
            {stop.client_name || `Paczka #${stop.final_order}`}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5 flex items-start gap-1">
            <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
            <span className="break-words" data-testid={`stop-address-${stop.final_order}`}>
              {fullAddress(stop) || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono flex-wrap">
            {/* Attribution — when the status was set by another courier,
                show that driver's chip so the current driver knows they
                shouldn't touch it (or that they finished it earlier). */}
            {stop.status_by && stop.status_by !== myUserId && stop.status_by_name && (() => {
              const tone = driverTone(stop.status_by, false);
              const verb = stop.status === "done" ? "Doręczył"
                : stop.status === "skipped" ? "Zamknął"
                : "Zmienił";
              return (
                <span
                  data-testid={`stop-owner-${stop.final_order}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 font-bold ${tone.bg}`}
                  title={
                    `Status ustawił: ${stop.status_by_name}` +
                    (stop.status_at ? `\n${new Date(stop.status_at).toLocaleString("pl-PL")}` : "")
                  }
                >
                  <User2 className="w-2.5 h-2.5" />
                  {verb}: {stop.status_by_name}
                </span>
              );
            })()}
            {stop.status === "skipped" && stop.undelivered_reason && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">
                {stop.undelivered_reason === "awizo" ? "Awizo"
                 : stop.undelivered_reason === "return" ? "Zwrot"
                 : stop.undelivered_reason === "redelivery" ? "Powtórka" : ""}
              </span>
            )}
            {stop.is_multipack && (
              <span
                data-testid={`multipack-badge-${stop.final_order}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-bold uppercase tracking-wider"
                title={`Multipack: ${stop.multipack_group_size} paczki pod tym adresem — 1× pełna stawka + ${stop.multipack_group_size - 1}× 0,50 zł`}
              >
                Multipack ×{stop.multipack_group_size}
              </span>
            )}
            {stop.is_fixed && (
              <span
                data-testid={`fixed-badge-${stop.final_order}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider"
                title="Stały punkt trasy — dodany automatycznie z szablonu"
              >
                📌 Stały
              </span>
            )}
            {(stop.time_from || stop.time_to) && (
              <span
                data-testid={`time-window-${stop.final_order}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-bold tracking-wider"
                title="Okno godzinowe doręczenia"
              >
                🕒 {stop.time_from || "…"}–{stop.time_to || "…"}
              </span>
            )}
            {stop.cargo_slot && (
              <span data-testid={`cargo-slot-badge-${stop.final_order}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 font-bold uppercase tracking-wider" title="Miejsce paczki na pace">
                {stop.cargo_bag && <span className="w-2.5 h-2.5 rounded-full ring-1 ring-black/20" style={{ background: bagHex(stop.cargo_bag) }} />}
                Paka: {slotLabel(stop.cargo_slot, stop.cargo_bag)}
              </span>
            )}
            {stop.parcel_number && (
              <span className="text-zinc-500 truncate max-w-[140px]" title={stop.parcel_number}>
                #{stop.parcel_number}
              </span>
            )}
            {stop.cod_amount > 0 && (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                POBRANIE {Number(stop.cod_amount).toLocaleString("pl-PL")} zł
              </span>
            )}
          </div>
        </div>
        {isSettled ? (
          <button
            onClick={(e) => { e.stopPropagation(); onReset(); }}
            data-testid={`btn-undo-${stop.final_order}`}
            className="p-1.5 rounded-lg hover:bg-white ring-1 ring-zinc-300 text-zinc-500 active:scale-90 transition-transform"
            title="Cofnij status"
          >
            <Undo2 className="w-4 h-4" />
          </button>
        ) : (
          <ChevronRight
            className={`w-4 h-4 text-zinc-400 transition-transform duration-300 ${
              menuOpen ? "rotate-90 text-orange-500" : ""
            }`}
          />
        )}
      </div>

      {/* Context menu — grid-rows expand animation (0fr → 1fr) so we can
          animate an "auto"-height panel with pure CSS. */}
      <div
        data-testid={`ctx-menu-${stop.final_order}`}
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: menuOpen ? "1fr" : "0fr" }}
        aria-hidden={!menuOpen}
      >
        <div className="overflow-hidden">
          <div className="px-3 pb-3 pt-1 flex flex-wrap gap-2 border-t border-zinc-100/70 bg-gradient-to-b from-zinc-50 to-transparent">
            {phoneDigits ? (
              <a
                href={`tel:${phoneDigits}`}
                onClick={(e) => { e.stopPropagation(); playClick(); }}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid={`ctx-call-${stop.final_order}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold uppercase tracking-widest shadow-sm active:scale-95 transition-transform"
              >
                <Phone className="w-4 h-4" /> Zadzwoń
                <span className="font-mono text-[10px] normal-case opacity-90">{stop.phone}</span>
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-zinc-100 text-zinc-400 text-xs font-bold uppercase tracking-widest">
                <Phone className="w-4 h-4" /> Brak numeru
              </span>
            )}
            {navUrl && (
              <a
                href={navUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.stopPropagation(); playClick(); }}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid={`ctx-nav-${stop.final_order}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white ring-1 ring-zinc-300 hover:bg-zinc-100 text-zinc-800 text-xs font-bold uppercase tracking-widest active:scale-95 transition-transform"
              >
                <MapPin className="w-4 h-4" /> Nawiguj
              </a>
            )}
            {menuOpen && stop.photo_id && (
              <PackagePhoto id={stop.photo_id} className="w-14 h-14" testId={`ctx-photo-${stop.final_order}`} />
            )}
            {stop.parcel_number && (
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white ring-1 ring-zinc-200 text-zinc-600 text-[11px] font-mono select-text">
                # {stop.parcel_number}
              </span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); playClick(); setMenuOpen(false); }}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid={`ctx-close-${stop.final_order}`}
              className="ml-auto inline-flex items-center gap-1 px-2.5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest text-zinc-500 hover:bg-zinc-200 active:scale-95 transition-transform"
            >
              <X className="w-3.5 h-3.5" /> Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone }) {
  const map = {
    zinc:    "from-zinc-700 to-zinc-900",
    emerald: "from-emerald-500 to-emerald-700",
    amber:   "from-amber-500 to-orange-700",
    cyan:    "from-cyan-500 to-blue-700",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${map[tone]} text-white p-3 ring-1 ring-white/10`}>
      <Icon className="absolute -right-3 -bottom-3 w-16 h-16 opacity-15" strokeWidth={1.5} />
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/70">{label}</div>
      <div className="text-2xl font-heading font-black mt-1">{value}</div>
    </div>
  );
}
